import { ArrowRight } from "lucide-react";
import { BackCoverFooter, PageShell, TopNav } from "@/app/components/brand-scaffold";
import { ContentHero } from "@/app/components/content-hero";
import { siteHeaderActionsEn, siteNavItemsEn } from "@/app/content-en";
import type { EnglishCaseStudy } from "@/app/seo-case-studies-en";

function renderSection(section: EnglishCaseStudy["sections"][number], idx: number) {
  switch (section.kind) {
    case "h2":
      return (
        <header key={idx} className="mt-12 first:mt-0">
          <h2 className="text-3xl font-black uppercase text-black sm:text-4xl">{section.text}</h2>
          {section.lead ? (
            <p className="mt-2 border-l-4 border-[#A3E635] pl-4 text-sm font-bold leading-6 text-zinc-600 sm:text-base">
              {section.lead}
            </p>
          ) : null}
        </header>
      );
    case "p":
      return (
        <p key={idx} className="mt-5 text-base font-bold leading-8 text-zinc-800">
          {section.text}
        </p>
      );
    case "ul":
      return (
        <ul key={idx} className="mt-5 space-y-3 border-l-4 border-black pl-5">
          {section.items.map((item) => (
            <li key={item} className="text-base font-bold leading-7 text-zinc-800">
              {item}
            </li>
          ))}
        </ul>
      );
    case "callout": {
      const palette: Record<typeof section.tone, { bg: string; tag: string }> = {
        info: { bg: "bg-[#3B82F6]/10 border-[#3B82F6]", tag: "bg-[#3B82F6] text-white" },
        warn: { bg: "bg-[#FACC15]/30 border-black", tag: "bg-black text-[#FACC15]" },
        success: { bg: "bg-[#A3E635]/40 border-black", tag: "bg-black text-[#A3E635]" },
      };
      const p = palette[section.tone];
      return (
        <aside key={idx} className={`mt-8 border-4 ${p.bg} p-5 sm:p-6`}>
          <div className={`inline-block border-2 border-black ${p.tag} px-2 py-0.5 text-[10px] font-black uppercase tracking-[0.2em]`}>
            {section.title}
          </div>
          <p className="mt-3 text-sm font-bold leading-7 text-zinc-900 sm:text-base">{section.body}</p>
        </aside>
      );
    }
    default:
      return null;
  }
}

export function CaseStudyLayout({ study }: { study: EnglishCaseStudy }) {
  return (
    <PageShell>
      <TopNav navItems={siteNavItemsEn} actionItems={siteHeaderActionsEn} homeHref="/en/" />

      <ContentHero
        eyebrow={`${study.eyebrow} · ${study.date}`}
        eyebrowColor="#A3E635"
        titleTop={study.title}
        lead={study.lead}
        meta={study.tags.map((tag) => `#${tag}`)}
        breadcrumbs={[
          { label: "English Home", href: "/en/" },
          { label: "Docs", href: "/en/docs/" },
          { label: "Case Study" },
        ]}
      />

      <main className="relative z-10 mx-auto w-full max-w-3xl px-4 py-12 sm:px-6 lg:px-8 lg:py-16">
        <article className="border-4 border-black bg-white/85 p-6 shadow-[8px_8px_0px_0px_rgba(0,0,0,1)] sm:p-8">
          {study.sections.map((section, idx) => renderSection(section, idx))}
        </article>

        <aside className="mt-12 border-[6px] border-black bg-[#A3E635]/85 p-6 shadow-[10px_10px_0px_0px_rgba(0,0,0,1)] sm:p-8">
          <p className="text-xs font-black uppercase tracking-[0.22em] text-black">NEXT</p>
          <h2 className="mt-3 text-2xl font-black uppercase text-black sm:text-3xl">Continue reading</h2>
          <div className="mt-5 grid gap-3">
            {study.nextLinks.map((link) => (
              <a
                key={link.href}
                href={link.href}
                className="group inline-flex items-center justify-between gap-3 border-4 border-black bg-white px-4 py-3 text-sm font-black uppercase text-black shadow-[4px_4px_0px_0px_rgba(0,0,0,1)] transition-transform hover:-translate-x-0.5 hover:-translate-y-0.5"
              >
                <span>{link.label}</span>
                <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-0.5" strokeWidth={3} />
              </a>
            ))}
          </div>
        </aside>
      </main>

      <BackCoverFooter />
    </PageShell>
  );
}
